import Dexie, { Table } from 'dexie';

/**
 * Outbox — IndexedDB-backed queue for writes that can't reach the
 * server right now (offline / load shedding / momentary 5xx).
 *
 * Phase 1.7a primitive. Phase 2 will wire the real order pipeline
 * through this. For v1 it's a thin layer that:
 *   - takes any POST/PUT/PATCH/DELETE
 *   - tries it
 *   - if the network is unreachable, parks the request in IndexedDB
 *     with an idempotency key
 *   - retries every 15s while online (and immediately on `online`)
 *
 * Cohesion rule 1: ONE outbox. Every write that needs offline
 * resilience goes through enqueueOrFire(). Nothing posts directly
 * with fetch() if it must survive a 5-second power cut.
 *
 * Cohesion rule 3: outbox publishes a state event on each change
 * (idle / syncing / errored / count). The ConnectionStatusBadge
 * subscribes via useOutbox().
 *
 * Idempotency: every queued request carries a client-generated UUID
 * (the saleUid pattern already used by CapturedSale). The server is
 * expected to dedupe on that key.
 */

export interface OutboxEntry {
  /** Auto-increment primary key. */
  id?: number;
  /** Idempotency key — sent in body or header so server can dedupe. */
  idempotencyKey: string;
  /** Plain-English description for the UI ("Cheese Burger ×2 sale"). */
  label: string;
  /** Fully-qualified URL or path. Same-origin. */
  url: string;
  /** HTTP method. */
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Serialised JSON body. We keep it as a string so it's queryable. */
  body: string;
  /** Optional content-type override (defaults to application/json). */
  contentType?: string;
  /** When the request was originally created on the device. */
  enqueuedAt: number;
  /** Number of times we've tried to send this and failed. */
  attempts: number;
  /** Last error message, if any. */
  lastError?: string;
  /** When the last send attempt happened. */
  lastAttemptAt?: number;
}

class OutboxDB extends Dexie {
  entries!: Table<OutboxEntry, number>;
  constructor() {
    super('habby-pos-outbox');
    this.version(1).stores({
      entries: '++id, idempotencyKey, enqueuedAt',
    });
  }
}

const db = new OutboxDB();

// ---------------------------------------------------------------------------
// Subscribe-able state for the UI badge
// ---------------------------------------------------------------------------

export interface OutboxState {
  online: boolean;
  pendingCount: number;
  isSyncing: boolean;
  lastError?: string;
}

type Listener = (s: OutboxState) => void;
const listeners = new Set<Listener>();
let state: OutboxState = {
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  pendingCount: 0,
  isSyncing: false,
};

function emit(patch: Partial<OutboxState>) {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn(state));
}

export function getOutboxState(): OutboxState {
  return state;
}

export function subscribeOutbox(fn: Listener): () => void {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

async function refreshPendingCount() {
  const count = await db.entries.count();
  emit({ pendingCount: count });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface SendInput {
  url: string;
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body: unknown;
  /** Idempotency key — if omitted we generate a UUID. */
  idempotencyKey?: string;
  /** Plain-English label used in the queued-list UI. */
  label?: string;
}

export interface SendResult<T = unknown> {
  status: 'sent' | 'queued';
  /** Present when status === 'sent'. */
  data?: T;
  /** Present when status === 'queued'. */
  entryId?: number;
  idempotencyKey: string;
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for very old browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Try to send the request immediately. On any non-network response
 * (200, 400, 401, 403, 500) we return the actual response — the
 * caller decides what to do. On a network/offline failure, we
 * enqueue it and the background flusher retries later.
 */
export async function sendOrEnqueue<T = unknown>(input: SendInput): Promise<SendResult<T>> {
  const idempotencyKey = input.idempotencyKey ?? uuid();
  const method = input.method ?? 'POST';
  const bodyJson = JSON.stringify({ ...(input.body as object), idempotencyKey });

  // If we're already offline, skip the network round-trip
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    const entryId = await enqueue({
      idempotencyKey,
      label: input.label ?? `${method} ${input.url}`,
      url: input.url,
      method,
      body: bodyJson,
    });
    return { status: 'queued', entryId, idempotencyKey };
  }

  try {
    const r = await fetch(input.url, {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Idempotency-Key': idempotencyKey },
      body: bodyJson,
    });
    // We got a real response — surface it to the caller. Errors at the
    // application layer (400/401/500) are NOT queued; only network
    // failures are.
    let data: unknown = undefined;
    try { data = await r.json(); } catch { /* no-body responses */ }
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}`);
    }
    return { status: 'sent', data: data as T, idempotencyKey };
  } catch (err) {
    // TypeError on fetch usually = network failure
    if (err instanceof TypeError) {
      const entryId = await enqueue({
        idempotencyKey,
        label: input.label ?? `${method} ${input.url}`,
        url: input.url,
        method,
        body: bodyJson,
      });
      return { status: 'queued', entryId, idempotencyKey };
    }
    // Otherwise re-throw so the caller can deal with application errors
    throw err;
  }
}

async function enqueue(entry: Omit<OutboxEntry, 'id' | 'enqueuedAt' | 'attempts'>): Promise<number> {
  const id = await db.entries.add({
    ...entry,
    enqueuedAt: Date.now(),
    attempts: 0,
  });
  await refreshPendingCount();
  return id as number;
}

// ---------------------------------------------------------------------------
// Background flusher
// ---------------------------------------------------------------------------

let flushTimer: ReturnType<typeof setInterval> | null = null;

async function flushOnce() {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  if (state.isSyncing) return;
  const pending = await db.entries.orderBy('enqueuedAt').toArray();
  if (pending.length === 0) return;

  emit({ isSyncing: true });
  try {
    for (const entry of pending) {
      try {
        const r = await fetch(entry.url, {
          method: entry.method,
          credentials: 'include',
          headers: {
            'Content-Type': entry.contentType ?? 'application/json',
            'X-Idempotency-Key': entry.idempotencyKey,
          },
          body: entry.body,
        });
        if (r.ok) {
          await db.entries.delete(entry.id!);
        } else {
          // Application error — don't keep retrying forever
          await db.entries.update(entry.id!, {
            attempts: entry.attempts + 1,
            lastAttemptAt: Date.now(),
            lastError: `HTTP ${r.status}`,
          });
          if (entry.attempts + 1 >= 5) {
            // Give up after 5 attempts (poison message)
            console.error('[outbox] dropping after 5 attempts:', entry);
            await db.entries.delete(entry.id!);
          }
        }
      } catch (err) {
        await db.entries.update(entry.id!, {
          attempts: entry.attempts + 1,
          lastAttemptAt: Date.now(),
          lastError: (err as Error).message,
        });
      }
    }
  } finally {
    await refreshPendingCount();
    emit({ isSyncing: false });
  }
}

export function startOutboxFlusher() {
  if (flushTimer) return;
  // Initial count
  refreshPendingCount();
  // Periodic
  flushTimer = setInterval(flushOnce, 15_000);
  // On reconnect — flush immediately
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      emit({ online: true });
      flushOnce();
    });
    window.addEventListener('offline', () => {
      emit({ online: false });
    });
  }
}

export function stopOutboxFlusher() {
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = null;
}
